import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Sidebar from "@/components/brand/Sidebar";
import BottomNav from "@/components/brand/BottomNav";
import PageEditor from "@/components/brand/PageEditor";
import BrandAgent from "@/components/brand/BrandAgent";
import BrandWriter from "@/components/brand/BrandWriter";
import BrandCreator from "@/components/brand/BrandCreator";
import SettingsHub from "@/components/brand/SettingsHub";
import SupportHub from "@/components/brand/SupportHub";
import GodModeBanner from "@/components/GodModeBanner";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import SuspendedScreen from "@/components/SuspendedScreen";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useBrandMeta } from "@/hooks/useBrandMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [brandsLoaded, setBrandsLoaded] = useState(false);
  const [activeBrandId, setActiveBrandId] = useState(null);
  const [pages, setPages] = useState([]);
  const [suspended, setSuspended] = useState(false);
  const [activePageId, setActivePageId] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [settingsTab, setSettingsTab] = useState("appearance");
  const [editMode, setEditMode] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newBrand, setNewBrand] = useState({ name: "", tagline: "" });
  // Guard against the URL-sync effect wiping the deep-link ?tool=/?page= params
  // before the pages-fetch effect has had a chance to read them on first paint.
  const urlParsedRef = useRef(false);

  const activeBrand = useMemo(() => brands.find((b) => b.id === activeBrandId), [brands, activeBrandId]);
  const activePage = useMemo(() => pages.find((p) => p.id === activePageId), [pages, activePageId]);
  // Keep the browser-tab title + favicon in sync with the active brand.
  useBrandMeta(activeBrand);
  const role = activeBrand?.role || "viewer";
  const canEdit = role === "manager" || role === "owner";
  const canUseAI = role === "creator" || role === "manager" || role === "owner";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/brands");
        setBrands(data);
        if (data.length && !activeBrandId) {
          const saved = localStorage.getItem("bh_active_brand");
          const brand = data.find((b) => b.id === saved) || data[0];
          setActiveBrandId(brand.id);
        }
      } catch (e) { toast.error(t("toasts.load_failed_brands")); }
      finally { setBrandsLoaded(true); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // No active brand after the brands-fetch resolved → bounce to the overview
  // page. That page already renders the "create your first brand" state, so
  // the Dashboard never has to flash its own empty UI.
  useEffect(() => {
    if (brandsLoaded && !activeBrand) {
      navigate("/brands", { replace: true });
    }
  }, [brandsLoaded, activeBrand, navigate]);

  useEffect(() => {
    if (!activeBrandId) return;
    localStorage.setItem("bh_active_brand", activeBrandId);
    (async () => {
      try {
        setSuspended(false);
        const { data } = await api.get(`/brands/${activeBrandId}/pages`);
        setPages(data);
        const urlParams = new URLSearchParams(window.location.search);
        const toolFromUrl = urlParams.get("tool");
        const pageFromUrl = urlParams.get("page");
        if (toolFromUrl && ["agent", "writer", "creator", "settings", "support"].includes(toolFromUrl)) {
          setActiveTool(toolFromUrl);
          const tabFromUrl = urlParams.get("tab");
          if (toolFromUrl === "settings" && tabFromUrl) setSettingsTab(tabFromUrl);
        } else {
          setActiveTool(null);
          if (data.length) {
            // Match URL `page=` against slug first, fall back to id for legacy links.
            const matched = pageFromUrl
              ? data.find((p) => p.slug === pageFromUrl) || data.find((p) => p.id === pageFromUrl)
              : null;
            const intro = matched || data.find((p) => p.name === "Introductie" || p.name === "Introduction") || data[0];
            setActivePageId(intro.id);
          }
        }
        urlParsedRef.current = true;
      } catch (e) {
        if (e?.response?.status === 423) {
          // Suspended by Noordzuid — swap the shell for the dedicated screen.
          setSuspended(true);
          urlParsedRef.current = true;
        } else {
          toast.error(t("toasts.load_failed_pages"));
        }
      }
    })();
  }, [activeBrandId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the URL in sync with the current page/tool selection so a refresh or a
  // shared link lands on the same section. Uses history.replaceState to avoid
  // triggering a react-router re-render.
  useEffect(() => {
    // Wait until the first pages-fetch has read the initial URL. Otherwise this
    // effect runs on mount with activeTool=null and would strip the deep-link
    // ?tool=support param before the fetch above gets to use it.
    if (!urlParsedRef.current) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("tool");
    url.searchParams.delete("page");
    url.searchParams.delete("tab");
    if (activeTool) {
      url.searchParams.set("tool", activeTool);
      if (activeTool === "settings" && settingsTab) url.searchParams.set("tab", settingsTab);
    } else if (activePageId) {
      const page = pages.find((p) => p.id === activePageId);
      url.searchParams.set("page", page?.slug || activePageId);
    }
    window.history.replaceState({}, "", url.toString());
  }, [activePageId, activeTool, settingsTab, pages]);

  useEffect(() => { if (!canEdit) setEditMode(false); }, [canEdit, activeBrandId]);

  // Set the brand accent as a global CSS variable so dashed-border focus-within reflects it live
  useEffect(() => {
    const accent = activeBrand?.color_accent || "#fe7f57";
    document.documentElement.style.setProperty("--bh-accent", accent);
  }, [activeBrand?.color_accent]);

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/brands", newBrand);
      setBrands((b) => [...b, data]);
      setActiveBrandId(data.id);
      setShowCreate(false);
      setNewBrand({ name: "", tagline: "" });
      toast.success(t("toasts.brand_created"));
    } catch { toast.error(t("toasts.create_failed")); }
  };

  const refreshPages = async () => {
    if (!activeBrandId) return;
    const { data } = await api.get(`/brands/${activeBrandId}/pages`);
    setPages(data);
  };

  const refreshBrand = async () => {
    if (!activeBrandId) return;
    const { data } = await api.get(`/brands/${activeBrandId}`);
    setBrands((bs) => bs.map((b) => (b.id === data.id ? { ...b, ...data } : b)));
  };

  return (
    <div className="min-h-screen bg-white text-[#1a1a1a]">
      <AnnouncementBanner />
      <GodModeBanner />
      {suspended ? (
        <SuspendedScreen brandName={activeBrand?.name} />
      ) : (
      <>
      <Sidebar
        brands={brands}
        activeBrandId={activeBrandId}
        pages={pages}
        activePageId={activePageId}
        activeTool={activeTool}
        onSelectPage={(id) => { setActivePageId(id); setActiveTool(null); }}
        onSelectTool={(tool, subTab) => { setActiveTool(tool); if (tool === "settings" && subTab) setSettingsTab(subTab); }}
        canEdit={canEdit}
        onAddPage={async () => {
          const name = prompt("Naam van de nieuwe pagina?");
          if (!name) return;
          const { data } = await api.post(`/brands/${activeBrandId}/pages`, { name });
          await refreshPages();
          setActivePageId(data.id);
          setActiveTool(null);
        }}
        user={user}
        role={role}
      />

      <BottomNav
        brand={activeBrand}
        pages={pages}
        activePageId={activePageId}
        activeTool={activeTool}
        onSelectPage={(id) => { setActivePageId(id); setActiveTool(null); }}
        onSelectTool={(tool, subTab) => { setActiveTool(tool); if (tool === "settings" && subTab) setSettingsTab(subTab); }}
        user={user}
        role={role}
      />

      <main className="min-h-screen flex flex-col md:ml-[var(--bh-sidebar-w,16rem)] pb-14 md:pb-0 transition-[margin] duration-300">
        {!activeBrand ? (
          /* Blank while loading — the useEffect above bounces to /brands
             as soon as the fetch resolves and there is still no active brand. */
          null
        ) : activeTool === "agent" ? (
          <div className="flex-1 h-screen overflow-hidden"><BrandAgent brand={activeBrand} canUse={canUseAI} /></div>
        ) : activeTool === "writer" ? (
          <div className="flex-1 h-screen overflow-hidden"><BrandWriter brand={activeBrand} role={role} /></div>
        ) : activeTool === "creator" ? (
          <div className="flex-1 h-screen overflow-hidden"><BrandCreator brand={activeBrand} role={role} /></div>
        ) : activeTool === "settings" ? (
          <div className="flex-1 h-screen overflow-y-auto bh-scroll">
            <SettingsHub brand={activeBrand} role={role} tab={settingsTab} setTab={setSettingsTab} onUpdated={refreshBrand} />
          </div>
        ) : activeTool === "support" ? (
          <div className="flex-1 h-screen overflow-y-auto bh-scroll">
            <SupportHub brand={activeBrand} role={role} />
          </div>
        ) : activePage ? (
          <div className="flex-1 h-screen overflow-y-auto bh-scroll">
            <PageEditor
              key={activePage.id}
              page={activePage}
              brand={activeBrand}
              editMode={editMode && canEdit}
              setEditMode={setEditMode}
              canEdit={canEdit}
              onChange={async (updates) => {
                const { data } = await api.put(`/pages/${activePage.id}`, updates);
                setPages((ps) => ps.map((p) => (p.id === data.id ? data : p)));
              }}
              onDelete={async () => {
                try {
                  await api.delete(`/pages/${activePage.id}`);
                  const { data } = await api.get(`/brands/${activeBrandId}/pages`);
                  setPages(data);
                  const intro = data.find((p) => p.name === "Introductie" || p.name === "Introduction") || data[0];
                  if (intro) setActivePageId(intro.id);
                  toast.success(t("toasts.page_deleted"));
                } catch { toast.error(t("toasts.delete_failed")); }
              }}
              onDuplicate={async () => {
                const { data } = await api.post(`/pages/${activePage.id}/duplicate`);
                await refreshPages();
                setActivePageId(data.id);
              }}
            />
          </div>
        ) : (
          <div className="p-12 text-gray-500">Geen pagina geselecteerd</div>
        )}
      </main>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-brand-dialog" className="bg-white">
          <DialogHeader><DialogTitle>Nieuw merk</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateBrand} className="space-y-4">
            <div><Label>Naam</Label><Input data-testid="new-brand-name" required value={newBrand.name} onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })} className="mt-1 bg-white" /></div>
            <div><Label>Tagline</Label><Input data-testid="new-brand-tagline" value={newBrand.tagline} onChange={(e) => setNewBrand({ ...newBrand, tagline: e.target.value })} className="mt-1 bg-white" /></div>
            <DialogFooter>
              <Button type="submit" data-testid="create-brand-submit" className="rounded-full bg-black text-white hover:bg-black/90">
                <Plus className="w-4 h-4 mr-2" /> Aanmaken
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
